/**
 * JobComments — timeline de comentários no job detail.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, MessageCircle, Pin, Send, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { Textarea } from "@/components/ui/textarea";
import { jobCommentsApi } from "@/lib/api-job-extras";
import { formatRelative } from "@/lib/format";
import { cn } from "@/lib/utils";

export function JobComments({ jobId }: { jobId: string }) {
  const qc = useQueryClient();
  const [body, setBody] = useState("");
  const [isInternal, setIsInternal] = useState(false);

  const list = useQuery({
    queryKey: ["job-comments", jobId],
    queryFn: () => jobCommentsApi.list(jobId),
    enabled: !!jobId,
  });

  const create = useMutation({
    mutationFn: () => jobCommentsApi.create(jobId, { body, is_internal: isInternal }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["job-comments", jobId] });
      setBody("");
      toast.success("Comentário publicado");
    },
    onError: (e: Error) => toast.error("Falha", { description: e.message }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => jobCommentsApi.remove(jobId, id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["job-comments", jobId] });
      toast.success("Removido");
    },
  });

  const togglePin = useMutation({
    mutationFn: ({ id, pinned }: { id: string; pinned: boolean }) =>
      jobCommentsApi.update(jobId, id, { pinned }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["job-comments", jobId] }),
  });

  return (
    <div className="space-y-4">
      {/* Compositor */}
      <div className="rounded-2xl border border-slate-200/60 dark:border-white/10 bg-white/70 dark:bg-card/40 p-4">
        <Textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Adicionar comentário (markdown suportado em breve)..."
          rows={3}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === "Enter" && body.trim()) {
              create.mutate();
            }
          }}
        />
        <div className="flex items-center justify-between mt-3">
          <label className="inline-flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
            <input
              type="checkbox"
              checked={isInternal}
              onChange={(e) => setIsInternal(e.target.checked)}
              className="rounded border-slate-300"
            />
            Nota interna (não visível pro cliente/técnico)
          </label>
          <Button
            size="sm"
            onClick={() => create.mutate()}
            disabled={create.isPending || !body.trim()}
          >
            {create.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
            Comentar
            <kbd className="ml-1 text-[10px] opacity-60">⌘↵</kbd>
          </Button>
        </div>
      </div>

      {/* Lista */}
      {list.isLoading ? (
        <p className="text-sm text-muted-foreground">Carregando…</p>
      ) : list.data?.items.length === 0 ? (
        <EmptyState
          icon={MessageCircle}
          title="Sem comentários"
          description="Adicione o primeiro comentário pra registrar contexto do atendimento."
        />
      ) : (
        <ol className="space-y-3">
          {list.data?.items.map((c) => (
            <li
              key={c.id}
              className={cn(
                "group rounded-xl border border-slate-200/60 dark:border-white/10",
                "bg-white/70 dark:bg-card/40 p-4",
                c.pinned && "ring-1 ring-amber-300/50 border-amber-200/60 dark:border-amber-500/30",
                c.is_internal && "border-l-4 border-l-amber-400",
              )}
            >
              <div className="flex items-start gap-3">
                <Avatar size="md">
                  <AvatarFallback name={c.author_name ?? "?"} />
                </Avatar>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-sm">{c.author_name ?? "—"}</span>
                    {c.author_role && (
                      <Badge variant="muted" size="sm" className="capitalize">{c.author_role}</Badge>
                    )}
                    {c.is_internal && <Badge variant="warning" size="sm">Interno</Badge>}
                    {c.pinned && <Badge variant="warning" size="sm"><Pin className="h-2.5 w-2.5" /> Fixado</Badge>}
                    <span className="text-xs text-muted-foreground ml-auto">{formatRelative(c.created_at)}</span>
                  </div>
                  <p className="mt-1.5 text-sm whitespace-pre-wrap">{c.body}</p>
                </div>
                <div className="opacity-0 group-hover:opacity-100 transition-opacity flex shrink-0 gap-0.5">
                  <Button
                    size="icon-sm"
                    variant="ghost"
                    onClick={() => togglePin.mutate({ id: c.id, pinned: !c.pinned })}
                    title={c.pinned ? "Desafixar" : "Fixar"}
                  >
                    <Pin className={cn("h-3.5 w-3.5", c.pinned && "fill-current")} />
                  </Button>
                  <Button
                    size="icon-sm"
                    variant="ghost"
                    onClick={() => remove.mutate(c.id)}
                    className="text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-500/10"
                    title="Excluir"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
